import { useUpdateEffect } from 'react-use';
import { useRef } from 'react';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import {
   getAutoTreatmentDaysCount,
   getPackagesCountForTreatmentDaysCount,
   PreciseDosage,
} from '~libs.shared.prescription';

import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';

type TreatmentDaysCountAnchor = 'packagesCount' | 'treatmentDaysCount';

type UseTreatmentDaysCountSyncOptions = {
   setValue: DraftPrescriptionsFormController['setValue'];
   medicine: MedicineSimple | undefined;
   preciseDosage: PreciseDosage;
   medicineSufficiencyCount: number | undefined;
   treatmentDaysCountFieldName: string;
   packagesCountFieldName: string;
   currentTreatmentDaysCount: number | null | undefined;
   currentPackagesCount: number | null | undefined;
   editable: boolean;
   enabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const useTreatmentDaysCountSync = (options: UseTreatmentDaysCountSyncOptions): void => {
   const { setValue, medicine, preciseDosage, treatmentDaysCountFieldName, packagesCountFieldName } = options;

   const treatmentDaysCount = options.currentTreatmentDaysCount ?? null;
   const packagesCount = options.currentPackagesCount ?? null;
   const autoTreatmentDaysCount = getAutoTreatmentDaysCount(options.medicineSufficiencyCount);

   const pendingTreatmentDaysCountRef = useRef<number | null | undefined>(undefined);
   const pendingPackagesCountRef = useRef<number | null | undefined>(undefined);
   const anchorRef = useRef<TreatmentDaysCountAnchor>('packagesCount');
   const manualTreatmentDaysCountRef = useRef<number | null>(null);

   const ownTreatmentDaysCount = pendingTreatmentDaysCountRef.current === treatmentDaysCount;
   const ownPackagesCount = pendingPackagesCountRef.current === packagesCount;

   const writeTreatmentDaysCount = (value: number | null): void => {
      pendingTreatmentDaysCountRef.current = value;
      setValue(treatmentDaysCountFieldName, value, { shouldValidate: true });
   };

   const writeTreatmentDaysCountForPackagesCount = (): void => {
      if (options.medicineSufficiencyCount === undefined) {
         return;
      }

      writeTreatmentDaysCount(autoTreatmentDaysCount);
   };

   const writePackagesCountForTreatmentDaysCount = (value: number | null = treatmentDaysCount): void => {
      const nextPackagesCount = getPackagesCountForTreatmentDaysCount({
         medicine: medicine,
         preciseDosage: preciseDosage,
         treatmentDaysCount: value,
      });

      if (nextPackagesCount) {
         pendingPackagesCountRef.current = nextPackagesCount;
         setValue(packagesCountFieldName, nextPackagesCount, { shouldValidate: true });
      }
   };

   useUpdateEffect(() => {
      if (!options.enabled) {
         return;
      }

      anchorRef.current = 'packagesCount';
      manualTreatmentDaysCountRef.current = null;
      writeTreatmentDaysCount(options.editable ? autoTreatmentDaysCount : null);

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [options.enabled]);

   useUpdateEffect(() => {
      if (!options.enabled) {
         return;
      }

      if (!options.editable) {
         manualTreatmentDaysCountRef.current = anchorRef.current === 'treatmentDaysCount' ? treatmentDaysCount : null;
         writeTreatmentDaysCount(null);
         return;
      }

      if (manualTreatmentDaysCountRef.current) {
         writeTreatmentDaysCount(manualTreatmentDaysCountRef.current);
         writePackagesCountForTreatmentDaysCount(manualTreatmentDaysCountRef.current);
         manualTreatmentDaysCountRef.current = null;
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [options.editable]);

   useUpdateEffect(() => {
      if (!options.enabled) {
         return;
      }

      pendingPackagesCountRef.current = undefined;

      if (ownPackagesCount) {
         return;
      }

      anchorRef.current = 'packagesCount';
      manualTreatmentDaysCountRef.current = null;
      writeTreatmentDaysCountForPackagesCount();

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [packagesCount]);

   useUpdateEffect(() => {
      if (!options.enabled) {
         return;
      }

      pendingTreatmentDaysCountRef.current = undefined;

      if (ownTreatmentDaysCount) {
         return;
      }

      if (!treatmentDaysCount) {
         anchorRef.current = 'packagesCount';
         return;
      }

      anchorRef.current = 'treatmentDaysCount';
      writePackagesCountForTreatmentDaysCount();

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [treatmentDaysCount]);

   useUpdateEffect(() => {
      if (!options.enabled) {
         return;
      }

      if (anchorRef.current === 'treatmentDaysCount') {
         writePackagesCountForTreatmentDaysCount();
         return;
      }

      writeTreatmentDaysCountForPackagesCount();

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [preciseDosage.frequency, preciseDosage.portion, preciseDosage.unit]);
};
